/**
 * T-947-01: next_action 标准化与完整保存
 *
 * 职责：
 * 1. 将后端返回的 next_action.type 归一化为前端标准动作类型
 * 2. 校验关键字段完整性，缺字段时标记 isActionPayloadComplete=false
 * 3. 供 gateway / mapper / hydrator / orchestrator 共同复用
 */

const STANDARD_NEXT_ACTION_TYPES = Object.freeze({
    POLL_AI_JOB: 'poll_ai_job',
    REFRESH_SUBJECT: 'refresh_subject',
    RETRY_CREATE_AI_JOB: 'retry_create_ai_job',
    RESTART_ANALYSIS_FLOW: 'restart_analysis_flow',
    MANUAL_RECOVERY: 'manual_recovery',
    REFRESH_PAYMENTS_STATUS: 'refresh_payments_status'
});

const LEGACY_NEXT_ACTION_TYPE_MAP = Object.freeze({
    restart_analysis: 'restart_analysis_flow',
    manual_recovery_required: 'manual_recovery'
});

const REQUIRED_FIELDS_BY_TYPE = Object.freeze({
    poll_ai_job: ['job_id'],
    refresh_subject: ['subject_id'],
    retry_create_ai_job: ['execution_id', 'retry_request_id'],
    restart_analysis_flow: [],
    manual_recovery: [],
    refresh_payments_status: []
});

const toTrimmedString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

/**
 * 将后端 next_action.type 归一化为前端标准动作类型。
 * 兼容旧值: restart_analysis -> restart_analysis_flow, manual_recovery_required -> manual_recovery
 */
export function normalizeNextActionType(rawType) {
    const normalized = toTrimmedString(rawType);
    if (!normalized) {
        return null;
    }

    if (Object.values(STANDARD_NEXT_ACTION_TYPES).includes(normalized)) {
        return normalized;
    }

    const mapped = LEGACY_NEXT_ACTION_TYPE_MAP[normalized];
    if (mapped) {
        return mapped;
    }

    return normalized;
}

/**
 * 标准化完整 next_action 载荷。
 * 输出 { type, method, endpoint, params, raw, missingRequiredFields, isActionPayloadComplete }
 */
export function normalizeNextActionPayload(raw, context = {}) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }

    const type = normalizeNextActionType(raw.type);
    if (!type) {
        return null;
    }

    const method = toTrimmedString(raw.method);
    const endpoint = toTrimmedString(raw.endpoint);
    const params = raw.params && typeof raw.params === 'object' && !Array.isArray(raw.params)
        ? { ...raw.params }
        : null;

    const readActionField = (snakeName, camelName = snakeName) => {
        const fromRaw = raw?.[snakeName] ?? raw?.[camelName];
        const fromParams = params?.[snakeName] ?? params?.[camelName];
        const fromContext = context?.[snakeName] ?? context?.[camelName];
        return toTrimmedString(fromRaw ?? fromParams ?? fromContext);
    };

    const resolvedJobId = readActionField('job_id', 'jobId');
    const resolvedSubjectId = readActionField('subject_id', 'subjectId');
    const resolvedExecutionId = readActionField('execution_id', 'executionId');
    const resolvedRetryRequestId = readActionField('retry_request_id', 'retryRequestId');

    const requiredFields = REQUIRED_FIELDS_BY_TYPE[type] || [];
    const missingRequiredFields = requiredFields.filter((field) => {
        if (field === 'job_id') return !resolvedJobId;
        if (field === 'subject_id') return !resolvedSubjectId;
        if (field === 'execution_id') return !resolvedExecutionId;
        if (field === 'retry_request_id') return !resolvedRetryRequestId;
        return !readActionField(field);
    });

    const hasMissingRequiredFields = missingRequiredFields.length > 0;
    const isActionPayloadComplete = !hasMissingRequiredFields;

    const defaultMethod = type === 'refresh_payments_status' ? 'GET' : null;
    const defaultEndpoint = type === 'refresh_payments_status' ? '/payments/status/' : null;

    return {
        type,
        method: method || defaultMethod,
        endpoint: endpoint || defaultEndpoint,
        params,
        raw,
        missingRequiredFields: hasMissingRequiredFields ? missingRequiredFields : null,
        isActionPayloadComplete,
        jobId: resolvedJobId,
        job_id: resolvedJobId,
        subjectId: resolvedSubjectId,
        subject_id: resolvedSubjectId,
        executionId: resolvedExecutionId,
        execution_id: resolvedExecutionId,
        retryRequestId: resolvedRetryRequestId,
        retry_request_id: resolvedRetryRequestId
    };
}

/**
 * 检查标准化后的 nextAction 是否具备完整的关键字段。
 */
export function isActionPayloadComplete(normalizedPayload) {
    return normalizedPayload?.isActionPayloadComplete === true;
}

export { STANDARD_NEXT_ACTION_TYPES, LEGACY_NEXT_ACTION_TYPE_MAP, REQUIRED_FIELDS_BY_TYPE };
